/**
 * Translate a tool call to a human-friendly description for the progress
 * sidebar. Falls back gracefully when input shape isn't what we expect —
 * the QA reviewer should never see "[object Object]" or raw selectors.
 */

const TYPE_BUTTON = /^(button|submit|reset|image)$/i;
const TYPE_TEXTISH = /^(text|email|password|search|tel|url|number)$/i;

/**
 * Strip a CSS selector down to the bit a human cares about.
 *   "#login-button"           → "login button"
 *   "[data-testid=cta-save]"  → "cta save"
 *   "input[type=email]"       → "email field"
 *   "button.btn-primary"      → "Primary button"
 *   "body > div.app > nav"    → "nav"
 */
function describeSelector(selector) {

    if (!selector || typeof selector !== 'string') return 'element';

    const s = selector.trim();

    // aria-label is the cleanest source — pull it out if present.
    const aria = s.match(/\[aria-label[~|*^$]?=["']?([^"'\]]+)/);
    if (aria) return `"${aria[1]}"`;

    // data-testid is almost as good.
    const testid = s.match(/\[data-testid[~|*^$]?=["']?([^"'\]]+)/);
    if (testid) return testid[1].replace(/[-_]/g, ' ');

    // Tag with type=foo (input[type=email], button[type=submit])
    const typed = s.match(/^([a-z]+)\[type[~|*^$]?=["']?([^"'\]]+)/i);
    if (typed) {

        const tag = typed[1].toLowerCase();
        const type = typed[2].toLowerCase();
        if (tag === 'button' || TYPE_BUTTON.test(type)) return `${type} button`;
        if (tag === 'input' && TYPE_TEXTISH.test(type)) return `${type} field`;
        return `${type} ${tag}`;

    }

    // Pure id selector — strip leading # and humanize dashes/underscores.
    const id = s.match(/^#([a-zA-Z][\w-]*)$/);
    if (id) return id[1].replace(/[-_]/g, ' ').toLowerCase();

    // Plain class on a tag (button.foo-bar) or just class (.foo-bar)
    const cls = s.match(/^(?:([a-z]+)\.|\.)([a-zA-Z][\w-]+)$/);
    if (cls) {

        const tag = cls[1] || 'element';
        const c = cls[2].replace(/[-_]/g, ' ').toLowerCase();
        return `${c} ${tag}`;

    }

    // Compound — last meaningful segment.
    const parts = s.split(/[>\s]+/).filter(Boolean);
    return parts[parts.length - 1] || s;

}

function pretty(value, max = 40) {

    if (value == null) return '';
    const s = String(value).trim();
    return s.length > max ? `${s.slice(0, max)}…` : s;

}

/**
 * Build a friendly label for a single tool call. Returns the string the
 * progress sidebar shows by default; the technical view continues to show
 * the raw tool name + input.
 */
export function humanLabel(toolName, toolInput, reasoning) {

    const inp = toolInput || {};

    switch (toolName) {

        case 'navigate': {

            try {

                const u = new URL(inp.url);
                return `Loading ${u.host}${u.pathname === '/' ? '' : u.pathname}`;

            } catch (_) {

                return `Loading ${pretty(inp.url, 60)}`;

            }

        }

        case 'click':
            return `Clicking ${describeSelector(inp.selector)}`;

        case 'type':
            return `Typing into ${describeSelector(inp.selector)}`;

        case 'type_credential':
            return `Entering ${inp.which || 'credential'}`;

        case 'press_key':
            return `Pressing ${inp.key || 'a key'}`;

        case 'wait_for':
            return `Waiting for ${describeSelector(inp.selector)}`;

        case 'screenshot':
            return inp.selector ? `Screenshotting ${describeSelector(inp.selector)}` : 'Taking screenshot';

        case 'capture_evidence':
            return `Saving "${pretty(inp.label, 60) || 'evidence'}"`;

        case 'assert_text':
            return `Verifying "${pretty(inp.text, 60)}"`;

        case 'get_dom_snapshot':
            return 'Reading page structure';

        case 'query_html':
            return inp.selector
                ? `Inspecting ${describeSelector(inp.selector)}`
                : 'Reading page source';

        case 'get_computed_styles':
            return `Checking style of ${describeSelector(inp.selector)}`;

        case 'set_progress':
            return null; // suppressed — meta tool

        case 'finish':
            return inp.passed === true ? 'Marking run passed'
                : inp.passed === false ? 'Marking run failed'
                : 'Finishing run';

        case 'note':
        case 'run_failed':
            return reasoning ? pretty(reasoning, 80) : 'Note';

        default:
            return toolName ? toolName.replace(/_/g, ' ') : 'Unknown action';

    }

}
